import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarCheck, Code2, Copy, ExternalLink, Loader2, Plus, Trash2, Unplug, Video } from "lucide-react";
import { crLocalToISO, isoToCRInput } from "@/lib/dateFormat";
import { toast } from "sonner";

const DAYS = [
  ["mon", "Lunes"], ["tue", "Martes"], ["wed", "Miércoles"], ["thu", "Jueves"],
  ["fri", "Viernes"], ["sat", "Sábado"], ["sun", "Domingo"],
];

const fmt = (iso, tz) =>
  new Date(iso).toLocaleString("es-CR", { dateStyle: "medium", timeStyle: "short", timeZone: tz || undefined });

export default function Booking() {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [appts, setAppts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mailLang, setMailLang] = useState("es");
  const [moving, setMoving] = useState(null);      // { appt, days }
  const [manual, setManual] = useState("");        // fecha y hora puestas a mano
  const [conflict, setConflict] = useState(null);  // { start, msg } antes de forzar
  const [notify, setNotify] = useState(false);     // la escuela mueve en silencio
  const [purging, setPurging] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [includePast, setIncludePast] = useState(false);

  const load = async (keepId) => {
    const [s, p, a] = await Promise.all([
      api.get("/calendar/status"),
      api.get("/booking/pages"),
      api.get("/booking/appointments").catch(() => ({ data: [] })),
    ]);
    setStatus(s.data);
    setPages(p.data || []);
    setAppts(a.data || []);
    const next = (p.data || []).find((x) => x.page_id === (keepId || activeId)) || (p.data || [])[0];
    setActiveId(next?.page_id || null);
    setDraft(next ? { ...next } : null);
  };

  useEffect(() => {
    load();
    const flag = new URLSearchParams(window.location.search).get("calendar");
    if (flag === "ok") toast.success("Google Calendar conectado");
    if (flag === "error") toast.error("No se pudo conectar con Google");
    if (flag === "norefresh") toast.error("Google no devolvió permiso permanente. Probá de nuevo.");
    if (flag) window.history.replaceState({}, "", "/dashboard/booking");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try {
      const r = await api.get("/calendar/auth-url");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión");
    }
  };

  const disconnect = async () => {
    await api.post("/calendar/disconnect");
    toast.success("Calendario desconectado");
    load();
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put(`/booking/pages/${activeId}`, patch);
      setDraft({ ...r.data });
      setPages(pages.map((p) => (p.page_id === activeId ? r.data : p)));
      toast.success("Guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const addPage = async () => {
    const r = await api.post("/booking/pages", { title: "Nueva página", meeting_types: [{ name: "Cita", minutes: 30 }] });
    await load(r.data.page_id);
    toast.success("Página creada");
  };

  const removePage = async () => {
    if (pages.length <= 1) {
      toast.error("Dejá al menos una página");
      return;
    }
    await api.delete(`/booking/pages/${activeId}`);
    setActiveId(null);
    await load();
    toast.success("Página eliminada");
  };

  const openMove = async (appt) => {
    setManual(isoToCRInput(appt.start_utc));
    setConflict(null);
    try {
      const r = await api.get(`/booking/appointments/${appt.id}/slots`);
      setMoving({ appt, days: r.data.days || [] });
    } catch (e) {
      // Sin Google Calendar no hay espacios que mostrar, pero la escuela igual
      // tiene que poder poner la clase a mano.
      setMoving({ appt, days: [], slotsError: e?.response?.data?.detail || "No se pudieron cargar los espacios" });
    }
  };

  const doMove = async (start, force = false) => {
    if (!start) { toast.error("Elegí la fecha y la hora"); return; }
    try {
      await api.post(`/booking/appointments/${moving.appt.id}/reschedule`, { start, notify, force });
      toast.success(notify ? "Cita movida · avisamos por correo" : "Cita movida sin avisar al cliente");
      setMoving(null);
      setConflict(null);
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        setConflict({ start, msg: e.response.data?.detail || "Ese horario está ocupado." });
        return;
      }
      toast.error(e?.response?.data?.detail || "No se pudo mover");
    }
  };

  const cancelAppt = async (id) => {
    await api.post(`/booking/appointments/${id}/cancel?notify=${notify}`);
    toast.success(notify ? "Cita cancelada · avisamos por correo" : "Cita cancelada sin avisar al cliente");
    load();
  };

  const deleteAppt = async (id) => {
    await api.delete(`/booking/appointments/${id}?notify=${notify}`);
    toast.success("Cita eliminada del panel y del calendario");
    load();
  };

  const fixLinks = async () => {
    setFixing(true);
    try {
      const r = await api.post("/booking/fix-meet-links");
      const { checked, fixed, failed } = r.data;
      toast.success(fixed
        ? `${fixed} de ${checked} clases quedaron con la sala del profesor${failed ? ` · ${failed} no se pudieron cambiar` : ""}`
        : `Revisé ${checked} clases reservadas: todas ya tenían la sala correcta`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron corregir los enlaces");
    } finally {
      setFixing(false);
    }
  };


  const purgeAppts = async () => {
    setSaving(true);
    try {
      const r = await api.post("/booking/appointments/purge", {
        notify, scope: includePast ? "all" : "upcoming",
      });
      toast.success(`${r.data.deleted} citas eliminadas`);
      setPurging(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron eliminar");
    } finally {
      setSaving(false);
    }
  };

  if (!draft || !status) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>;
  }

  const setHours = (day, idx, key, value) => {
    const weekly = { ...(draft.weekly_hours || {}) };
    const list = [...(weekly[day] || [])];
    list[idx] = { ...list[idx], [key]: value };
    weekly[day] = list;
    setDraft({ ...draft, weekly_hours: weekly });
  };
  const addWindow = (day) => {
    const weekly = { ...(draft.weekly_hours || {}) };
    weekly[day] = [...(weekly[day] || []), { start: "09:00", end: "12:00" }];
    setDraft({ ...draft, weekly_hours: weekly });
  };
  const removeWindow = (day, idx) => {
    const weekly = { ...(draft.weekly_hours || {}) };
    weekly[day] = (weekly[day] || []).filter((_, i) => i !== idx);
    setDraft({ ...draft, weekly_hours: weekly });
  };
  const setQuestion = (idx, key, value) => {
    const qs = [...(draft.questions || [])];
    qs[idx] = { ...qs[idx], [key]: value };
    setDraft({ ...draft, questions: qs });
  };

  const setType = (idx, key, value) => {
    const types = [...(draft.meeting_types || [])];
    types[idx] = { ...types[idx], [key]: value };
    setDraft({ ...draft, meeting_types: types });
  };

  return (
    <div>
      <PageHeader
        title="Reservas y citas"
        subtitle="Cada página es independiente: su propio enlace, duración y horario. La disponibilidad sale de tu Google Calendar."
        testId="booking-header"
      />

      <Card className="p-5 mb-6" data-testid="booking-connection-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 text-brand-blue" /> Google Calendar
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {status.connected
                ? `Conectado como ${status.email}`
                : "Conectá tu cuenta de la escuela para leer tus horas ocupadas y crear los eventos"}
            </p>
          </div>
          {status.connected ? (
            <Button variant="outline" size="sm" onClick={disconnect} data-testid="calendar-disconnect-btn">
              <Unplug className="h-4 w-4" /> Desconectar
            </Button>
          ) : (
            <Button size="sm" className="btn-brand" onClick={connect} data-testid="calendar-connect-btn">
              <Video className="h-4 w-4" /> Conectar mi Google Calendar
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-5 mb-6" data-testid="booking-fix-links-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Video className="h-4 w-4 text-brand-blue" /> Enlaces de las clases ya reservadas
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Les pone la misma sala que el profesor ve en la tarjeta de su clase. Cambia el evento
              del calendario y la app; no envía ningún correo.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fixLinks} disabled={fixing}
                  data-testid="booking-fix-links-btn">
            {fixing ? "Corrigiendo…" : "Corregir enlaces"}
          </Button>
        </div>
      </Card>


      <Card className="p-4 mb-6" data-testid="booking-email-preview-card">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-900 mr-2">Ver los correos que recibe la persona:</span>
          <div className="inline-flex rounded-full border border-slate-200 p-0.5 mr-2">
            {["es", "en"].map((l) => (
              <button key={l} onClick={() => setMailLang(l)} data-testid={`booking-mail-lang-${l}`}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ${
                        mailLang === l ? "bg-brand-blue text-white" : "text-slate-500"}`}>
                {l}
              </button>
            ))}
          </div>
          {[["new", "Confirmación"], ["reminder", "Recordatorio"], ["reschedule", "Reprogramada"], ["cancel", "Cancelada"]].map(([k, lbl]) => (
            <a key={k} href={`${API_BASE}/api/booking/email-preview?kind=${k}&lang=${mailLang}`} target="_blank" rel="noreferrer"
               data-testid={`booking-email-preview-${k}`}
               className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-brand-blue hover:text-brand-blue">
              {lbl}
            </a>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="booking-page-tabs">
        {pages.map((p) => (
          <button
            key={p.page_id}
            onClick={() => { setActiveId(p.page_id); setDraft({ ...p }); }}
            data-testid={`booking-tab-${p.page_id}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              p.page_id === activeId ? "bg-brand-blue text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {p.title || "Sin título"}
            {p.meeting_types?.[0] ? ` · ${p.meeting_types[0].minutes} min` : ""}
            {!p.enabled && <span className="ml-2 text-[10px] uppercase opacity-70">off</span>}
          </button>
        ))}
        <Button variant="outline" size="sm" onClick={addPage} data-testid="booking-add-page">
          <Plus className="h-4 w-4" /> Nueva página
        </Button>
      </div>

      <Card className="p-5 mb-6" data-testid="booking-page-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900">{draft.title || "Página de reservas"}</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{draft.enabled ? "Activa" : "Desactivada"}</span>
            <Switch
              checked={!!draft.enabled}
              onCheckedChange={(v) => save({ enabled: v, slug: draft.slug || "" })}
              data-testid="booking-enabled-switch"
            />
            <Button variant="ghost" size="sm" className="text-red-600" onClick={removePage}
                    data-testid="booking-delete-page">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Enlace de esta página</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs text-slate-500 whitespace-nowrap">/reservar/</span>
              <Input
                value={draft.slug || ""}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                placeholder="reuniones"
                data-testid="booking-slug-input"
              />
            </div>
            {draft.public_url && (
              <div className="flex items-center gap-3 mt-2">
                <button
                  className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1"
                  onClick={() => { navigator.clipboard.writeText(draft.public_url); toast.success("Enlace copiado"); }}
                  data-testid="booking-copy-link"
                >
                  <Copy className="h-3 w-3" /> Copiar enlace
                </button>
                <a href={draft.public_url} target="_blank" rel="noreferrer"
                   className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1"
                   data-testid="booking-open-link">
                  <ExternalLink className="h-3 w-3" /> Abrir
                </a>
                <button
                  className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `<iframe src="${draft.public_url}" style="width:100%;min-height:900px;border:0;" loading="lazy" title="${draft.title || "Reservas"}"></iframe>`
                    );
                    toast.success("Código para tu web copiado");
                  }}
                  data-testid="booking-copy-embed"
                >
                  <Code2 className="h-3 w-3" /> Copiar código para mi web
                </button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Título que ve la persona</Label>
            <Input className="mt-1.5" value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                   placeholder="Reuniones con Wagner" data-testid="booking-title-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Mensaje de bienvenida (opcional)</Label>
            <Input className="mt-1.5" value={draft.intro || ""} onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
                   placeholder="Elegí el espacio que mejor te funcione y te confirmo por correo"
                   data-testid="booking-intro-input" />
          </div>
          <div>
            <Label className="text-xs">Zona horaria de mi agenda</Label>
            <Input className="mt-1.5" value={draft.timezone || ""} onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                   placeholder="America/Costa_Rica" data-testid="booking-timezone-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Aviso mínimo (horas)</Label>
              <Input className="mt-1.5" type="number" min={0} value={draft.min_notice_hours ?? 4}
                     onChange={(e) => setDraft({ ...draft, min_notice_hours: Number(e.target.value) })}
                     data-testid="booking-notice-input" />
            </div>
            <div>
              <Label className="text-xs">Colchón (min)</Label>
              <Input className="mt-1.5" type="number" min={0} value={draft.buffer_minutes ?? 15}
                     onChange={(e) => setDraft({ ...draft, buffer_minutes: Number(e.target.value) })}
                     data-testid="booking-buffer-input" />
            </div>
            <div>
              <Label className="text-xs">Días adelante</Label>
              <Input className="mt-1.5" type="number" min={1} value={draft.days_ahead ?? 30}
                     onChange={(e) => setDraft({ ...draft, days_ahead: Number(e.target.value) })}
                     data-testid="booking-horizon-input" />
            </div>
          </div>
          <div className="md:col-span-2 flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div>
              <Label className="text-sm">Clases del ciclo del estudiante</Label>
              <p className="text-[11px] text-slate-500 mt-1 max-w-xl">
                Activá esto solo en la página de clases: la app valida el correo del estudiante, le
                permite reservar hasta las sesiones que le quedan del ciclo y deja la clase programada.
                La sesión se consume al marcar asistencia, como siempre. Cancelar con menos de 12 horas
                consume la sesión.
              </p>
            </div>
            <Switch
              checked={!!draft.consumes_sessions}
              onCheckedChange={(v) => save({ consumes_sessions: v })}
              data-testid="booking-consumes-sessions-switch"
            />
          </div>
        </div>
      </Card>

      <Card className="p-5 mb-6" data-testid="booking-types-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-900">Tipos de cita de esta página</h3>
          <Button size="sm" variant="outline"
                  onClick={() => setDraft({ ...draft, meeting_types: [...(draft.meeting_types || []), { id: "", name: "", minutes: 30 }] })}
                  data-testid="booking-add-type">
            <Plus className="h-4 w-4" /> Agregar
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Dejá uno solo si querés que la persona no tenga que elegir.</p>
        <div className="space-y-3">
          {(draft.meeting_types || []).map((mt, idx) => (
            <div key={mt.id || idx} className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Nombre</Label>
                <Input className="mt-1.5" value={mt.name || ""} onChange={(e) => setType(idx, "name", e.target.value)}
                       data-testid={`booking-type-name-${idx}`} />
              </div>
              <div className="w-28">
                <Label className="text-xs">Minutos</Label>
                <Input className="mt-1.5" type="number" min={5} value={mt.minutes || 30}
                       onChange={(e) => setType(idx, "minutes", Number(e.target.value))}
                       data-testid={`booking-type-minutes-${idx}`} />
              </div>
              <Button variant="ghost" size="sm" className="text-red-600"
                      onClick={() => setDraft({ ...draft, meeting_types: draft.meeting_types.filter((_, i) => i !== idx) })}
                      data-testid={`booking-type-remove-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 mb-6" data-testid="booking-questions-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-900">Preguntas del formulario</h3>
          <Button size="sm" variant="outline" data-testid="booking-add-question"
                  onClick={() => setDraft({ ...draft, questions: [...(draft.questions || []), { id: "", label: "", type: "text", options: [], required: true }] })}>
            <Plus className="h-4 w-4" /> Pregunta
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Se agregan después del WhatsApp y todas son obligatorias. Las respuestas te llegan en el
          correo y quedan guardadas en cada cita.
        </p>
        <div className="space-y-4">
          {(draft.questions || []).length === 0 && (
            <p className="text-sm text-slate-500">Todavía no hay preguntas propias.</p>
          )}
          {(draft.questions || []).map((q, idx) => (
            <div key={q.id || idx} className="rounded-xl border border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs">Pregunta</Label>
                  <Input className="mt-1.5" value={q.label || ""} data-testid={`booking-q-label-${idx}`}
                         placeholder="¿Cuál es tu nivel de español?"
                         onChange={(e) => setQuestion(idx, "label", e.target.value)} />
                </div>
                <div className="w-40">
                  <Label className="text-xs">Tipo</Label>
                  <select className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-2 text-sm"
                          value={q.type} data-testid={`booking-q-type-${idx}`}
                          onChange={(e) => setQuestion(idx, "type", e.target.value)}>
                    <option value="text">Texto libre</option>
                    <option value="choice">Una opción</option>
                    <option value="multi">Varias opciones</option>
                  </select>
                </div>

                <Button variant="ghost" size="sm" className="text-red-600" data-testid={`booking-q-remove-${idx}`}
                        onClick={() => setDraft({ ...draft, questions: draft.questions.filter((_, i) => i !== idx) })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {q.type !== "text" && (
                <div>
                  <Label className="text-xs">Opciones (una por línea)</Label>
                  <textarea rows={3} data-testid={`booking-q-options-${idx}`}
                            className="mt-1.5 w-full rounded-md border border-slate-200 p-2 text-sm"
                            value={(q.options || []).join("\n")}
                            onChange={(e) => setQuestion(idx, "options", e.target.value.split("\n"))} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 mb-6" data-testid="booking-hours-card">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Horario de atención de esta página</h3>
        <p className="text-xs text-slate-500 mb-4">
          Apagá los días que no atendés. Dentro de las franjas encendidas la app solo ofrece lo que
          tengas libre en Google.
        </p>
        <div className="space-y-3">
          {DAYS.map(([key, label]) => {
            const windows = (draft.weekly_hours || {})[key] || [];
            const open = windows.length > 0;
            return (
            <div key={key} className={`flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 ${open ? "" : "bg-slate-50"}`}>
              <label className="flex items-center gap-2 w-40 cursor-pointer">
                <Switch
                  checked={open}
                  onCheckedChange={(v) => {
                    const weekly = { ...(draft.weekly_hours || {}) };
                    weekly[key] = v ? [{ start: "08:00", end: "17:00" }] : [];
                    setDraft({ ...draft, weekly_hours: weekly });
                  }}
                  data-testid={`booking-${key}-enabled`}
                />
                <span className={`text-sm ${open ? "text-slate-700" : "text-slate-400"}`}>{label}</span>
              </label>
              {!open && <span className="text-xs text-slate-400">Cerrado</span>}
              {windows.map((w, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <Input className="w-28" type="time" value={w.start}
                         onChange={(e) => setHours(key, idx, "start", e.target.value)}
                         data-testid={`booking-${key}-start-${idx}`} />
                  <span className="text-slate-400">–</span>
                  <Input className="w-28" type="time" value={w.end}
                         onChange={(e) => setHours(key, idx, "end", e.target.value)}
                         data-testid={`booking-${key}-end-${idx}`} />
                  <Button variant="ghost" size="sm" className="text-red-600 px-2"
                          onClick={() => removeWindow(key, idx)} data-testid={`booking-${key}-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {open && (
                <Button variant="ghost" size="sm" onClick={() => addWindow(key)} data-testid={`booking-${key}-add`}>
                  <Plus className="h-3.5 w-3.5" /> Franja
                </Button>
              )}
            </div>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end mb-8">
        <Button className="btn-brand" disabled={saving} data-testid="booking-save-btn"
                onClick={() => save({
                  slug: draft.slug || "",
                  title: draft.title || "",
                  intro: draft.intro || "",
                  timezone: draft.timezone || "America/Costa_Rica",
                  min_notice_hours: draft.min_notice_hours ?? 4,
                  buffer_minutes: draft.buffer_minutes ?? 15,
                  days_ahead: draft.days_ahead ?? 30,
                  weekly_hours: draft.weekly_hours,
                  meeting_types: draft.meeting_types,
                  questions: (draft.questions || []).filter((q) => (q.label || "").trim()),
                })}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar cambios
        </Button>
      </div>

      <Card className="p-5" data-testid="booking-appointments-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-slate-900">Próximas citas de la escuela</h3>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <Switch checked={notify} onCheckedChange={setNotify} data-testid="booking-notify-switch" />
              Avisar al cliente por correo
            </label>
            {appts.length > 0 && (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200"
                      onClick={() => setPurging(true)} data-testid="booking-purge-btn">
                <Trash2 className="h-4 w-4" /> Eliminar todas
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-2 mb-4">
          Se ven las citas de todas las páginas de reservas de la escuela, incluido el día de hoy
          completo. {notify
            ? "Al cancelar, mover o eliminar una cita se le avisa por correo al cliente (y a Google)."
            : "Las cancelaciones, cambios y borrados se hacen en silencio: el cliente no recibe correo."}
        </p>
        {appts.length === 0 ? (
          <p className="text-sm text-slate-500">Todavía no hay reservas.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {appts.map((a) => (
              <li key={a.id} className="py-3 flex flex-wrap items-center justify-between gap-2"
                  data-testid={`booking-appt-${a.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {a.type_name} · {a.guest_name}
                    {a.status === "cancelled" && <span className="ml-2 text-xs text-red-600">cancelada</span>}
                    {!a.mine && a.host_name && (
                      <span className="ml-2 text-[11px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5"
                            data-testid={`booking-appt-host-${a.id}`}>
                        con {a.host_name}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {fmt(a.start_utc, draft.timezone)} · {a.guest_email}{a.guest_phone ? ` · ${a.guest_phone}` : ""}
                  </div>
                  {(a.answers || []).length > 0 && (
                    <ul className="mt-1.5 space-y-0.5" data-testid={`booking-appt-answers-${a.id}`}>
                      {a.answers.map((ans, i) => (
                        <li key={i} className="text-xs text-slate-600">
                          <span className="text-slate-400">{ans.label}:</span> {ans.value}
                        </li>
                      ))}
                    </ul>
                  )}
                  {a.notes && <p className="text-xs text-slate-600 mt-1"><span className="text-slate-400">Comentario:</span> {a.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {a.meet_link && (
                    <a href={a.meet_link} target="_blank" rel="noreferrer"
                       className="text-xs text-brand-blue hover:underline" data-testid={`booking-appt-link-${a.id}`}>
                      Enlace
                    </a>
                  )}
                  {a.status === "confirmed" && (
                    <>
                      <Button variant="outline" size="sm"
                              onClick={() => openMove(a)} data-testid={`booking-appt-move-${a.id}`}>
                        Mover
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600"
                              onClick={() => cancelAppt(a.id)} data-testid={`booking-appt-cancel-${a.id}`}>
                        Cancelar
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-600"
                          onClick={() => deleteAppt(a.id)} data-testid={`booking-appt-delete-${a.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={!!moving} onOpenChange={(v) => !v && setMoving(null)}>
        <DialogContent className="max-w-lg" data-testid="booking-move-dialog">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg">
              Mover la cita de {moving?.appt?.guest_name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            {moving?.slotsError
              ? `No se pudieron leer los espacios libres (${moving.slotsError}). Podés poner la fecha y la hora a mano.`
              : "Los botones son los espacios libres del Google Calendar del profesor. Si necesitás otro horario, ponelo a mano abajo."}
            {" "}{notify
              ? "Al confirmar se mueve el evento y le avisamos por correo a la persona."
              : "Al confirmar se mueve el evento sin mandar ningún correo."}
          </p>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Label className="text-xs text-slate-600">Fecha y hora a mano (hora de Costa Rica)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <Input type="datetime-local" value={manual} className="h-9 w-[220px] bg-white"
                     onChange={(e) => { setManual(e.target.value); setConflict(null); }}
                     data-testid="booking-move-manual-input" />
              <Button size="sm" onClick={() => doMove(crLocalToISO(manual))}
                      data-testid="booking-move-manual-btn">
                Mover a este horario
              </Button>
            </div>
            {conflict && (
              <div className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5"
                   data-testid="booking-move-conflict">
                <p className="text-xs text-amber-800">{conflict.msg}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => doMove(conflict.start, true)}
                          data-testid="booking-move-force-btn">
                    Moverla igual
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConflict(null)}
                          data-testid="booking-move-conflict-cancel">
                    Elegir otro
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto space-y-4 pr-1">
            {(moving?.days || []).length === 0 && !moving?.slotsError && (
              <p className="text-sm text-slate-500">No hay espacios disponibles.</p>
            )}
            {(moving?.days || []).map((d) => (
              <div key={d.date}>
                <div className="text-xs uppercase tracking-[0.15em] text-brand-orange mb-2">
                  {new Date(`${d.date}T12:00:00Z`).toLocaleDateString("es-CR", {   // tz-ok: mediodía fijo + timeZone UTC
                    weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {d.slots.map((s) => (
                    <button key={s} onClick={() => doMove(s)}
                            data-testid={`booking-move-slot-${s}`}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:border-brand-blue hover:text-brand-blue">
                      {new Date(s).toLocaleTimeString("es-CR", {   // tz-ok: timeZone explícito abajo
                        hour: "2-digit", minute: "2-digit", timeZone: draft.timezone || undefined })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={purging} onOpenChange={setPurging}>
        <DialogContent className="max-w-md" data-testid="booking-purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg">Eliminar todas las citas</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Se cancelan en tu Google Calendar (el evento desaparece de tu agenda) y se borran de este
            panel. Esta acción no se puede deshacer.
          </p>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="booking-purge-notify-switch" />
            Avisarle por correo a cada cliente
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={includePast} onCheckedChange={setIncludePast} data-testid="booking-purge-past-switch" />
            Incluir también las citas ya pasadas
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPurging(false)} data-testid="booking-purge-cancel">
              Mejor no
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={saving}
                    onClick={purgeAppts} data-testid="booking-purge-confirm">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Sí, eliminar todas
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
